import {queryOptions, UseQueryOptions} from '@tanstack/react-query';
import {LAYOUT_QUERY_KEY_MAP} from '../api/layout/constant';
import {getLayout} from '../api/layout/getLayout';
import {getLayoutReferenceViewData} from '../api/layout/getLayoutReferenceViewData';
import {FetcherOptions} from '../axios/axiosBffFetch';
import {requestContext} from '../common/requestContext';
import {ApiStatusCodeVo} from '../common/shared/api/karina/vo/enums/ApiStatusCodeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {PagewebErrorType} from '../errors/PagewebErrorType.enum';
import {CustomApolloError} from '../graphql/api/CustomApolloError';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createDynamicLayout} from '../graphql/factory/DynamicLayout';
import {createLayoutWithLayoutVo} from '../graphql/factory/Layout';
import {createLayoutReferenceViewDataList} from '../graphql/factory/LayoutReferenceViewDataList';
import {
  DynamicLayoutReference,
  DynamicLayoutReferenceQueryVariables,
  Layout,
  LayoutListQueryVariables,
  LayoutReferenceQueryVariables,
  LayoutReferenceViewData,
  MainQueryVariables,
  Maybe,
} from '../graphql/generated/types';

export const layoutFactory = {
  /**
   * 레이아웃 조회
   */
  layout: (
    variables?: MainQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout | null, PagewebError, Layout | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LAYOUT_QUERY_KEY_MAP.layout(variables),
      queryFn: async (): Promise<Layout | null> => {
        const {screenUid, type} = variables || {};
        const {result} = await getLayout(screenUid, fetcherOptions);

        if (!result) {
          throw createSimpleApiResponseError('레이아웃을 찾을 수 없음');
        }

        const layout = createLayoutWithLayoutVo(
          requestContext?.requestInfo?.user,
          {
            layoutVo: result,
            type,
            screenUid,
          }
        );
        return layout;
      },
      ...options,
    }),

  layoutList: (
    variables?: LayoutListQueryVariables,
    options?: Omit<
      UseQueryOptions<Layout[] | null, PagewebError, Layout[] | null>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LAYOUT_QUERY_KEY_MAP.layoutList(variables),
      queryFn: async (): Promise<Layout[] | null> => {
        const {screenUidList: _screenUidList, type} = variables || {};
        const screenUidList = Array.isArray(_screenUidList)
          ? _screenUidList
          : [_screenUidList];

        const layoutListResult = await Promise.all(
          screenUidList?.map(async screenUid => {
            const layoutResponse = await getLayout(screenUid, fetcherOptions);

            const {result} = layoutResponse;
            if (!result) {
              throw createSimpleApiResponseError('Layout 을 찾을 수 없음');
            }
            const layout = createLayoutWithLayoutVo(
              requestContext?.requestInfo?.user,
              {
                layoutVo: result,
                type,
                screenUid,
              }
            );
            return layout;
          })
        );

        return layoutListResult;
      },
      ...options,
    }),

  layoutReference: (
    variables?: LayoutReferenceQueryVariables,
    options?: Omit<
      UseQueryOptions<
        LayoutReferenceViewData[] | null,
        PagewebError,
        LayoutReferenceViewData[] | null
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LAYOUT_QUERY_KEY_MAP.layoutReference(variables),
      queryFn: async (): Promise<LayoutReferenceViewData[] | null> => {
        if (!variables) {
          return null;
        }
        const {dataKey, sectionType, metaInfoType} = variables;

        const layoutResponse = await getLayoutReferenceViewData({
          dataKeyList: dataKey ? [dataKey] : [],
          options: fetcherOptions,
        });
        const {result} = layoutResponse;

        if (!result) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.KarinaApiStatusError,
            resultCode: ApiStatusCodeVo.Fail,
            message: '카리나 실패',
          });
        }

        return createLayoutReferenceViewDataList(
          requestContext?.requestInfo?.user,
          {
            dataKeyList: dataKey ? [dataKey] : [],
            layout: result,
            sectionType: sectionType,
            metaInfoType: metaInfoType,
          }
        );
      },
      ...options,
    }),

  dynamicLayoutReference: (
    variables?: DynamicLayoutReferenceQueryVariables,
    options?: Omit<
      UseQueryOptions<
        Maybe<DynamicLayoutReference> | undefined,
        PagewebError,
        Maybe<DynamicLayoutReference> | undefined
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: LAYOUT_QUERY_KEY_MAP.dynamicLayoutReference(variables),
      queryFn: async (): Promise<Maybe<DynamicLayoutReference> | undefined> => {
        if (!variables) {
          return undefined;
        }
        const {dataKey, sectionType, metaInfoType} = variables;

        const layoutResponse = await getLayoutReferenceViewData({
          dataKeyList: dataKey ? [dataKey] : [],
          options: fetcherOptions,
        });
        const {result} = layoutResponse;

        if (!result) {
          throw new CustomApolloError({
            isSerializedPagewebError: true,
            type: PagewebErrorType.KarinaApiStatusError,
            resultCode: ApiStatusCodeVo.Fail,
            message: '카리나 실패',
          });
        }

        return createDynamicLayout(requestContext?.requestInfo?.user, {
          dataKey: dataKey,
          layout: result,
          sectionType: sectionType,
          metaInfoType: metaInfoType,
        });
      },
      ...options,
    }),
};
